import { inject, Injectable } from "@angular/core";
import { BehaviorSubject } from "rxjs";
import { map } from "rxjs/operators";
import { Area } from "tht-impianti-cl";
import { AreaSelectionState, initialAreaSelectionState } from "./area-selection.state";
import { AreaSelectionActions, setAreas, setSelectedArea, setLoading } from "./area-selection.actions";
import { areaSelectionReducer } from "./area-selection.reducer";
import { selectAreas, selectSelectedAreaId, selectSelectedArea, selectAreaLoading, selectAreaOptions } from "./area-selection.selectors";
import { TranslateService } from "../shared/util/translate.service";

@Injectable({
  providedIn: "root"
})
export class AreaSelectionStore {
  private readonly STORAGE_KEY = "selectedAreaId";
  private state$ = new BehaviorSubject<AreaSelectionState>(this.loadInitialState());
  private translateService = inject(TranslateService);

  // Public observables
  public areas$ = this.state$.asObservable().pipe(
    map(state => selectAreas(state))
  );

  public selectedAreaId$ = this.state$.asObservable().pipe(
    map(state => selectSelectedAreaId(state))
  );

  public selectedArea$ = this.state$.asObservable().pipe(
    map(state => selectSelectedArea(state))
  );

  public loading$ = this.state$.asObservable().pipe(
    map(state => selectAreaLoading(state))
  );

  public areaOptions$ = this.state$.asObservable().pipe(
    map(state => selectAreaOptions(state, this.translateService))
  );

  // Getters for current state
  get currentState(): AreaSelectionState {
    return this.state$.getValue();
  }

  get selectedAreaId(): string | null {
    return selectSelectedAreaId(this.currentState);
  }

  get selectedArea(): Area | null {
    return selectSelectedArea(this.currentState);
  }

  // Actions
  dispatchSetAreas(areas: Area[]): void {
    this.dispatch(setAreas(areas));
  }

  dispatchSetSelectedArea(areaId: string | null): void {
    this.dispatch(setSelectedArea(areaId));
    this.saveToLocalStorage(areaId);
  }

  dispatchSetLoading(loading: boolean): void {
    this.dispatch(setLoading(loading));
  }

  dispatchClearSelectedArea(): void {
    this.saveToLocalStorage(null);
  }

  private dispatch(action: AreaSelectionActions): void {
    const currentState = this.state$.getValue();
    const newState = areaSelectionReducer(currentState, action);
    this.state$.next(newState);
  }

  private loadInitialState(): AreaSelectionState {
    const savedAreaId = localStorage.getItem(this.STORAGE_KEY);
    return {
      ...initialAreaSelectionState,
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
      selectedAreaId: savedAreaId || null
    };
  }

  private saveToLocalStorage(areaId: string | null): void {
    if (areaId === null) {
      localStorage.removeItem(this.STORAGE_KEY);
    } else {
      localStorage.setItem(this.STORAGE_KEY, areaId);
    }
  }
} 